<template>
    <div class="request-list">
        <Table
            :columns="tblColumns"
            :data="requestsDataList"
            :loading="isLoading"
            no-data-text="No time disputes for this year"
            class="request-list__table"
        />

        <div class="request-list__cards">
            <Spin v-if="isLoading" fix />
            <p v-if="!requestsDataList.length" class="request-list__empty">No time disputes for this year</p>
            <div v-for="row in requestsDataList" :key="row._id" class="request-card">
                <div class="request-card__head">
                    <div>
                        <div class="request-card__dates">{{ formatDate(row.requestedStart) }}</div>
                        <div class="request-card__meta">
                            Requested {{ formatTimeRange(row.requestedData) }} ({{ formatHours(row.requestedHrs) }})
                        </div>
                        <div class="request-card__meta">
                            Initial {{ hasInitial(row) ? formatTimeRange(row.currentData) : '—' }} ({{
                                formatHours(row.initialHrs)
                            }})
                        </div>
                    </div>
                    <span :class="['request-status', `request-status--${row.status}`]">
                        <span class="request-status__dot" />{{ formatStatus(row.status) }}
                    </span>
                </div>
                <div class="request-card__foot">
                    <span class="request-card__filed">Filed {{ formatDate(row.createdAt) }}</span>
                    <div class="request-actions">
                        <Button size="small" icon="md-eye" @click="handleView(row)">View</Button>
                        <template v-if="isPending(row)">
                            <Button size="small" icon="md-create" @click="handleEdit(row)">Edit</Button>
                            <Button
                                size="small"
                                icon="md-trash"
                                class="request-actions__cancel"
                                @click="handleDelete(row)"
                            >
                                Delete
                            </Button>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <Page
            v-if="totalCount > limit"
            :total="totalCount"
            :current="currentPage"
            :page-size="limit"
            size="small"
            class="request-list__pagination"
            @on-change="handleOnpageChanged"
        />
        <ViewRequestModal ref="ViewRequest" @create="handleReloadList" />
        <UpdateRequestModal ref="UpdateRequest" @create="handleReloadList" />
        <DeleteRequestModal ref="DeleteRequest" @create="handleReloadList" />
    </div>
</template>

<script>
import moment from 'moment';
import ViewRequestModal from './ViewRequestModal';
import UpdateRequestModal from './UpdateRequestModal';
import DeleteRequestModal from './DeleteRequestModal';

export default {
    components: {
        ViewRequestModal,
        UpdateRequestModal,
        DeleteRequestModal,
    },
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        requestsDataList: {
            type: Array,
            default: () => [],
        },
        currentPage: {
            type: Number,
            default: 1,
        },
        totalCount: {
            type: Number,
            default: 0,
        },
        limit: {
            type: Number,
            default: 10,
        },
    },

    data() {
        return {
            tblColumns: [
                {
                    title: 'Date',
                    key: 'requestedStart',
                    minWidth: 110,
                    render: (h, params) => h('span', this.formatDate(params.row.requestedStart)),
                },
                {
                    title: 'Initial Time',
                    key: 'initialTime',
                    minWidth: 170,
                    render: (h, params) =>
                        this.hasInitial(params.row)
                            ? h('span', this.formatTimeRange(params.row.currentData))
                            : h('span', { class: 'request-list__muted' }, '—'),
                },
                {
                    title: 'Requested Time',
                    key: 'requestedTime',
                    minWidth: 170,
                    render: (h, params) => h('span', this.formatTimeRange(params.row.requestedData)),
                },
                {
                    title: 'Initial Hours',
                    key: 'initialHrs',
                    minWidth: 110,
                    render: (h, params) => h('span', this.formatHours(params.row.initialHrs)),
                },
                {
                    title: 'Requested Hours',
                    key: 'requestedHrs',
                    minWidth: 120,
                    render: (h, params) => h('span', this.formatHours(params.row.requestedHrs)),
                },
                {
                    title: 'Status',
                    key: 'status',
                    minWidth: 120,
                    render: (h, params) =>
                        h('span', { class: ['request-status', `request-status--${params.row.status}`] }, [
                            h('span', { class: 'request-status__dot' }),
                            this.formatStatus(params.row.status),
                        ]),
                },
                {
                    title: 'Actions',
                    width: 160,
                    align: 'right',
                    render: (h, params) => {
                        const iconButton = (content, icon, onClick, extraClass = '') =>
                            h('Tooltip', { props: { placement: 'top', content, transfer: true } }, [
                                h('Button', {
                                    class: ['request-actions__icon', extraClass],
                                    props: { icon, shape: 'circle' },
                                    on: { click: onClick },
                                }),
                            ]);
                        const buttons = [iconButton('View', 'md-eye', () => this.handleView(params.row))];
                        if (this.isPending(params.row)) {
                            buttons.push(
                                iconButton('Edit', 'md-create', () => this.handleEdit(params.row)),
                                iconButton(
                                    'Delete',
                                    'md-trash',
                                    () => this.handleDelete(params.row),
                                    'request-actions__cancel'
                                )
                            );
                        }
                        return h('div', { class: 'request-actions' }, buttons);
                    },
                },
            ],
        };
    },

    methods: {
        formatDate(date) {
            return date ? moment(date).utc().format('YYYY-MM-DD') : '—';
        },
        formatTimeRange(data) {
            if (!data || !data.clockInTime) return '—';
            const start = moment(data.clockInTime).utc().format('hh:mm A');
            const end = data.clockOutTime ? moment(data.clockOutTime).utc().format('hh:mm A') : '—';
            return `${start} – ${end}`;
        },
        formatHours(hours) {
            if (!hours) return '—';
            return `${Number(hours).toFixed(0)} hours`;
        },
        hasInitial(row) {
            return !!(row.currentData && row.initialHrs);
        },
        formatStatus(status) {
            if (!status) return '—';
            return status.charAt(0).toUpperCase() + status.slice(1);
        },
        isPending(row) {
            return !!row.status && row.status.includes('pending');
        },
        handleView(row) {
            this.$refs.ViewRequest.openModal(row, false);
        },
        handleEdit(row) {
            this.$refs.UpdateRequest.openModal(row, false);
        },
        handleDelete(row) {
            this.$refs.DeleteRequest.openModal(row, 'delete');
        },
        handleReloadList() {
            this.$emit('reload');
        },
        handleOnpageChanged(page) {
            this.$emit('on-page-change', page);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../shared/request-table.scss';
</style>
